import { useState, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ArrowLeftIcon, ArrowRightIcon } from '@untitledui-icons/react/line'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/base/buttons'
import { Progress } from '@/components/application/progress-steps/progress-steps'
import { useStartOnboarding, useOnboardingStatus } from '@/hooks/api/useOnboarding'
import { usePermissions } from '@/hooks/api'
import { LocationStep, DetailsStep, ProcessingStep } from './components'

// Session storage key for persisting wizard state
const STORAGE_KEY = 'onboarding_wizard_state'

interface WizardState {
  activeStep: number
  placeId: string
  templateId: number
  customData: string
  enableRestaurantDetection: boolean
  taskId: string | null
}

const DEFAULT_STATE: WizardState = {
  activeStep: 0,
  placeId: '',
  templateId: 180,
  customData: '',
  enableRestaurantDetection: true,
  taskId: null,
}

/**
 * Load wizard state from session storage
 */
function loadWizardState(): WizardState {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (stored) {
      return { ...DEFAULT_STATE, ...JSON.parse(stored) }
    }
  } catch {
    // Ignore parsing errors
  }
  return DEFAULT_STATE
}

/**
 * Save wizard state to session storage
 */
function saveWizardState(state: WizardState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clear wizard state from session storage
 */
function clearWizardState(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore storage errors
  }
}

/**
 * Onboarding wizard page.
 * Guides admins/partners through setting up a new customer with an AI assistant.
 */
export function OnboardingPage() {
  const { t } = useTranslation()
  const { isAdmin, isPartner } = usePermissions()
  const showTemplateId = isAdmin || isPartner

  // Initialize state from session storage
  const [wizardState, setWizardState] = useState<WizardState>(loadWizardState)

  // Destructure for easier access
  const { activeStep, placeId, templateId, customData, enableRestaurantDetection, taskId } = wizardState

  // Build progress steps with status based on activeStep
  const progressItems = useMemo(
    () => [
      {
        title: t('resources.onboarding.steps.location'),
        description: '',
        status: activeStep > 0 ? 'complete' : activeStep === 0 ? 'current' : 'incomplete',
      },
      {
        title: t('resources.onboarding.steps.details'),
        description: '',
        status: activeStep > 1 ? 'complete' : activeStep === 1 ? 'current' : 'incomplete',
      },
      {
        title: t('resources.onboarding.steps.review'),
        description: '',
        status: activeStep > 2 ? 'complete' : activeStep === 2 ? 'current' : 'incomplete',
      },
    ] as const,
    [t, activeStep]
  )

  // API mutations
  const startOnboarding = useStartOnboarding()
  
  // Poll for status when we have a task ID
  const { data: onboardingStatus, error: statusError } = useOnboardingStatus(taskId)

  // Persist state changes to session storage (for in-page refreshes)
  useEffect(() => {
    saveWizardState(wizardState)
  }, [wizardState])

  // Reset state when navigating away so users start fresh on return
  useEffect(() => {
    return () => clearWizardState()
  }, [])

  // Clear state when onboarding completes successfully
  useEffect(() => {
    if (onboardingStatus?.status === 'completed') {
      clearWizardState()
    }
  }, [onboardingStatus?.status])

  // Update state helper
  const updateState = useCallback((updates: Partial<WizardState>) => {
    setWizardState((prev) => ({ ...prev, ...updates }))
  }, [])

  // Handle next step
  const handleNext = useCallback(async () => {
    if (activeStep === 0) {
      // Validate location
      if (!placeId) {
        toast.error(t('resources.onboarding.errors.select_location'))
        return
      }
      updateState({ activeStep: 1 })
    } else if (activeStep === 1) {
      // Start onboarding process
      updateState({ activeStep: 2 })
      
      try {
        const result = await startOnboarding.mutateAsync({
          place_id: placeId,
          template_id: templateId,
          custom_data: customData,
          enable_restaurant_detection: enableRestaurantDetection,
        })
        updateState({ taskId: result.task_id })
      } catch (error) {
        console.error('Onboarding failed:', error)
        toast.error(t('resources.onboarding.errors.start_failed'))
        updateState({ activeStep: 1 })
      }
    }
  }, [activeStep, placeId, templateId, customData, enableRestaurantDetection, updateState, startOnboarding, t])

  // Handle back step
  const handleBack = useCallback(() => {
    if (activeStep > 0 && activeStep < 2) {
      updateState({ activeStep: activeStep - 1 })
    }
  }, [activeStep, updateState])

  // Determine if Next button should be disabled
  const isNextDisabled = activeStep === 0 && !placeId

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t('resources.onboarding.title')}
        subtitle={t('resources.onboarding.subtitle')}
      />

      <div className="flex-1 overflow-auto p-6">
        <div className="mx-auto max-w-3xl">
          {/* Step indicator - using official Untitled UI Progress Steps */}
          <Progress.IconsWithText
            type="number"
            orientation="horizontal"
            size="md"
            items={progressItems}
            className="mb-10"
          />

          {/* Step content */}
          <div className="min-h-[400px]">
            {activeStep === 0 && (
              <LocationStep
                placeId={placeId}
                templateId={templateId}
                onPlaceIdChange={(id) => updateState({ placeId: id })}
                onTemplateIdChange={(id) => updateState({ templateId: id })}
                showTemplateId={showTemplateId}
              />
            )}

            {activeStep === 1 && (
              <DetailsStep
                customData={customData}
                enableRestaurantDetection={enableRestaurantDetection}
                onCustomDataChange={(data) => updateState({ customData: data })}
                onEnableRestaurantDetectionChange={(enabled) =>
                  updateState({ enableRestaurantDetection: enabled })
                }
              />
            )}

            {activeStep === 2 && (
              <ProcessingStep
                status={onboardingStatus ?? null}
                isLoading={startOnboarding.isPending}
                error={statusError?.message || startOnboarding.error?.message || null}
                taskId={taskId}
              />
            )}
          </div>

          {/* Navigation buttons */}
          {activeStep < 2 && (
            <div className="mt-8 flex items-center justify-between border-t border-gray-200 pt-6 dark:border-gray-700">
              <Button
                color="secondary"
                onPress={handleBack}
                isDisabled={activeStep === 0}
                iconLeading={ArrowLeftIcon}
              >
                {t('resources.generic.back')}
              </Button>
              <Button
                color="primary"
                onPress={handleNext}
                isDisabled={isNextDisabled}
                isPending={startOnboarding.isPending}
                iconTrailing={ArrowRightIcon}
              >
                {t('resources.generic.next')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default OnboardingPage
